import type { PlayerEpisode } from './types'

/**
 * F-178: the lock screen and the headset buttons (Media Session API): the
 * episode's title, the show and the cover, play/pause, −15 s/+30 s, seek and
 * next. Browsers without it just skip.
 */

interface SessionActions {
  play: () => void
  pause: () => void
  seekBackward: () => void
  seekForward: () => void
  seekTo: (seconds: number) => void
  next: (() => void) | null
  stop: () => void
}

const session = (): MediaSession | null =>
  typeof navigator !== 'undefined' && 'mediaSession' in navigator ? navigator.mediaSession : null

const setHandler = (media: MediaSession, action: MediaSessionAction, handler: MediaSessionActionHandler | null) => {
  try {
    media.setActionHandler(action, handler)
  } catch {
    /* An action this browser does not know. */
  }
}

export const updateMediaSession = (episode: PlayerEpisode, coverUrl: string | null, actions: SessionActions): void => {
  const media = session()
  if (!media || typeof MediaMetadata === 'undefined') return
  media.metadata = new MediaMetadata({
    title: episode.title,
    artist: episode.show?.title || '',
    album: episode.show?.title || '',
    artwork: coverUrl ? [{ src: coverUrl, sizes: '512x512' }] : []
  })
  setHandler(media, 'play', () => actions.play())
  setHandler(media, 'pause', () => actions.pause())
  setHandler(media, 'seekbackward', () => actions.seekBackward())
  setHandler(media, 'seekforward', () => actions.seekForward())
  setHandler(media, 'seekto', (details) => { if (typeof details.seekTime === 'number') actions.seekTo(details.seekTime) })
  setHandler(media, 'nexttrack', actions.next ? () => actions.next?.() : null)
  setHandler(media, 'stop', () => actions.stop())
}

export const updateMediaSessionPosition = (audio: HTMLAudioElement): void => {
  const media = session()
  if (!media) return
  media.playbackState = audio.paused ? 'paused' : 'playing'
  if (!media.setPositionState || !Number.isFinite(audio.duration) || audio.duration <= 0) return
  try {
    media.setPositionState({ duration: audio.duration, playbackRate: audio.playbackRate || 1, position: Math.min(audio.currentTime, audio.duration) })
  } catch {
    /* A position past the duration while the metadata settles. */
  }
}

export const clearMediaSession = (): void => {
  const media = session()
  if (!media) return
  media.metadata = null
  media.playbackState = 'none'
  for (const action of ['play', 'pause', 'seekbackward', 'seekforward', 'seekto', 'nexttrack', 'stop'] as MediaSessionAction[]) setHandler(media, action, null)
}
